import React, { useEffect, useState } from "react";
import styles from "./QBankQuestionImage.module.css";

export interface QBankImage {
    url: string;
    alt_text: string;
    width?: number;
    height?: number;
}

interface Props {
    image?: QBankImage | null;
    compact?: boolean;
}

const QBankQuestionImage: React.FC<Props> = ({ image, compact = false }) => {
    const [viewerOpen, setViewerOpen] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setFailed(false);
        setViewerOpen(false);
    }, [image?.url]);

    useEffect(() => {
        if (!viewerOpen) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setViewerOpen(false);
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [viewerOpen]);

    if (!image?.url) return null;
    if (failed) {
        return <p className={styles.fallback}>{image.alt_text || "Question image unavailable"}</p>;
    }

    return (
        <>
            <button
                type="button"
                className={`${styles.previewButton} ${compact ? styles.compact : ""}`}
                onClick={(event) => {
                    event.stopPropagation();
                    setViewerOpen(true);
                }}
                aria-label={`Open full-size question image: ${image.alt_text}`}
            >
                <img
                    className={styles.image}
                    src={image.url}
                    alt={image.alt_text}
                    width={image.width || undefined}
                    height={image.height || undefined}
                    loading="lazy"
                    decoding="async"
                    onError={() => setFailed(true)}
                />
                <span className={styles.zoomHint}>View full size</span>
            </button>

            {viewerOpen && (
                <div
                    className={styles.viewer}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Question image viewer"
                    onClick={() => setViewerOpen(false)}
                >
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={() => setViewerOpen(false)}
                        aria-label="Close image viewer"
                    >
                        ×
                    </button>
                    <img
                        className={styles.fullImage}
                        src={image.url}
                        alt={image.alt_text}
                        onClick={(event) => event.stopPropagation()}
                    />
                </div>
            )}
        </>
    );
};

export default QBankQuestionImage;
